import type { WatchHandle } from 'vue';
import type { InStationDetectionSettings } from '../common/useSystemConfig';
import type { PositionValidationArea } from '~/stores/geolocations/geolocationStore';
import { GEOLOCATION_SUBSCRIBER_FEATURES } from '~/stores/geolocations/geolocationStore';
import { StationRepository } from '~/repository/StationRepository';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';
import { judgeAreaPosition } from '~/composables/geolocations/judgements/areaJudgement';

/** 駅エリア判定で利用する画面ID */
const DISPLAY_ID = '0-00';

/**
 * 駅エリア検知機能を提供するコンポジション関数。
 *
 * 起動停止と駅情報取得・エリア判定処理を担う。位置情報の取得と保持はgeolocationStoreが担う。
 * 判定結果はstoreに書き戻し、コンポーネントはstoreを参照する。
 */
export const usePositionValidation = () => {
  const logger = useLogger(DISPLAY_ID);
  const { getInStationDetectionSettings } = useSystemConfig(DISPLAY_ID);
  const { requestWatch, releaseWatch } = useGeoLocations();
  const store = useGeolocationStore();

  /** 駅エリア検知のシステム設定 */
  let settings: InStationDetectionSettings | undefined = undefined;

  /** エリア判定を定期実行する interval の識別子 */
  let intervalId: number | null = null;

  /** 非同期の検証開始要求が最新かどうかを判定するための連番 */
  let activationRequestId = 0;

  /** startWatchStateの多重watch登録防止用 */
  let watchHandler: WatchHandle | null = null;

  /**
   * エリア内判定処理。
   * store.currentPosition と store.validationArea を読み、矩形内判定・連続成功カウントを行い、
   * 結果をstoreに書き戻す。判定間隔はsetIntervalのcheckIntervalで制御される。
   */
  const judgeArea = () => {
    if (!settings) {
      return;
    }

    // store上の現在状態を判定関数の入力に変換する。
    const result = judgeAreaPosition({
      position: store.currentPosition,
      acquisitionError: store.acquisitionError,
      area: store.validationArea,
      consecutiveSuccess: store.consecutiveSuccess,
      settings,
    });

    // 判定関数はloggerに依存しないため、返却された補足情報をcomposable側でログ出力する。
    if (result.log) {
      logger.info('位置情報取得', {
        latitude: result.log.latitude,
        longitude: result.log.longitude,
        accuracy: result.log.accuracy,
      });
    }

    // エリア内外の判定結果ログは、旧実装と同じ粒度でcomposable側から出力する。
    if (result.status === 'inside') {
      logger.info(`エリア内にいます (${result.log?.consecutiveSuccess}/${result.log?.stayThreshold}回)`);
    }
    else if (result.status === 'outside') {
      logger.info('エリア外にいます', {
        latitude: result.log?.latitude,
        longitude: result.log?.longitude,
        area: result.log?.area,
      });
    }

    // 判定関数が返したstore更新値だけを反映する。waiting時は更新しない。
    if (result.areaFields) {
      store.setAreaFields(result.areaFields);
    }
  };

  /** 検証開始 */
  const startValidation = async (area: PositionValidationArea, requestId: number) => {
    if (intervalId !== null) {
      stopValidation();
    }
    const detectionSettings = await getInStationDetectionSettings();
    if (requestId !== activationRequestId) {
      return;
    }
    settings = detectionSettings;
    store.setValidationArea(area);
    store.setStationAreaStayThreshold(detectionSettings.stayThreshold);
    store.setAreaFields({
      consecutiveSuccess: 0,
      isInsideArea: false,
      validationError: null,
    });
    requestWatch(GEOLOCATION_SUBSCRIBER_FEATURES.STATION_AREA);

    logger.info('位置検証開始', { area });
    intervalId = window.setInterval(judgeArea, settings.checkInterval);
  };

  /**
   * 検証停止
   *
   * @param options.resetStatus エリア判定関連の派生ステータスを初期化するか（既定: true）。
   *   駅情報取得失敗時など、validationError を維持したい場合は false を指定する。
   */
  const stopValidation = (options: { resetStatus?: boolean; } = {}) => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    if (options.resetStatus !== false) {
      store.resetAreaStatus();
    }
    releaseWatch(GEOLOCATION_SUBSCRIBER_FEATURES.STATION_AREA);
    logger.info('位置検証停止');
  };

  const loginUserStore = useLoginUserStore();
  const { loginUser } = storeToRefs(loginUserStore);
  const { canRunStationAreaValidation } = useStationAreaConditions();
  const stationRepository = new StationRepository(DISPLAY_ID);

  /**
   * 状態を監視し、エリア検知の開始と停止を条件によって制御する。
   *
   * 以下を全て満たす場合にエリア検知を開始する。それ以外は停止する。
   * - PC端末ではない
   * - ログイン状態である
   * - ログインユーザの駅エリア検知フラグがtrue
   * - 作業駅が設定されている
   * - 荷役作業権限がある
   * - 代理ログイン中でない
   */
  const startWatchState = () => {
    if (watchHandler !== null) {
      return;
    }
    let prevWorkingStation: StationWithOrganization | undefined = undefined;
    watchHandler = watch([canRunStationAreaValidation, loginUser], async () => {
      const requestId = ++activationRequestId;

      if (!canRunStationAreaValidation.value) {
        prevWorkingStation = undefined;
        stopValidation();
        return;
      }

      // 検証中かつ作業駅に変更がない場合は何もしない
      const currentWorkingStation = loginUser.value?.workingStation;
      if (
        intervalId !== null
        && prevWorkingStation?.stationCd === currentWorkingStation?.stationCd
        && prevWorkingStation?.stationSubCd === currentWorkingStation?.stationSubCd
      ) {
        return;
      }
      prevWorkingStation = currentWorkingStation;

      // 駅情報を取得して検証開始
      try {
        const response = await stationRepository.getStationInfo();
        if (!response.data?.stationInfo) {
          throw new Error();
        }
        const area: PositionValidationArea = {
          lat1: response.data.stationInfo.lat1,
          lat2: response.data.stationInfo.lat2,
          lon1: response.data.stationInfo.lon1,
          lon2: response.data.stationInfo.lon2,
          isAreaDetectionEnabled: response.data.stationInfo.isAreaDetectionEnabled,
        };
        if (requestId !== activationRequestId || !canRunStationAreaValidation.value) {
          return;
        }
        const latestWorkingStation = loginUser.value?.workingStation;
        if (
          latestWorkingStation?.stationCd !== currentWorkingStation?.stationCd
          || latestWorkingStation?.stationSubCd !== currentWorkingStation?.stationSubCd
        ) {
          return;
        }
        await startValidation(area, requestId);
      }
      catch {
        logger.error('駅情報の取得に失敗しました');
        if (requestId !== activationRequestId || !canRunStationAreaValidation.value) {
          return;
        }
        store.setAreaFields({
          validationError: '駅情報の取得に失敗しました',
          isInsideArea: false,
        });
        stopValidation({ resetStatus: false });
      }
    }, {
      deep: true,
      immediate: true,
    });
  };

  return {
    startWatchState,
  };
};
